// src/pages/AdminDashboard.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { doc, setDoc } from "firebase/firestore";
import Card from "../ui/Card";
import Stat from "../ui/Stat";
import Table from "../ui/Table";
import PilotOperationsSection from "../components/PilotOperationsSection";
import { useAuth } from "../context/AuthContext";
import { fetchOrgSessionsFiltered } from "../utils/orgData";
import { detectSessionFlag } from "../utils/sessionFlags";
import { fetchCheckinWindow } from "../utils/checkins";
import { db } from "../firebase";
import { buildFollowUpQueueRows, formatFollowUpDate, getFollowUpDisplayStatus, mergeSessionFollowUp } from "../utils/followUp";
import { getSessionDisplayName, getSessionDisplayText } from "../utils/sessionPrivacy";
import { formatSupportTextForAudience } from "../utils/summary";
import { resolveCurrentRecordSummary } from "../utils/sessionScope";
import {
  getPostSessionAutomaticEmailLabel,
  getPostSessionFinalizationLabel,
  getPostSessionManualResendLabel,
  getPostSessionReminderLabel,
  getSessionLifecycleLabel,
  normalizePostSessionContinuation,
} from "../utils/summaryDelivery";
import { fetchFlagCases, getFlagCaseStatusLabel, isFinalFlagStatus } from "../utils/flagCases";
import { getSessionRecencyTimestamp, logSessionListDiagnostics, sortSessionsByRecency } from "../utils/sessionOrdering";

const POS = ["great","good","better","improved","progress","relieved","hopeful","calm"];
const NEG = ["sad","down","stressed","anxious","angry","overwhelmed","stuck","blocked","panic","tired","burnout","lonely","frustrated","worried","depressed"];

function normalizeRecord(r) {
  const createdAt = r?.createdAt || r?.date || Date.now();
  return {
    ...r,
    id: r?.id || createdAt,
    createdAt,
    updatedAt: r?.updatedAt || null,
    lastMessageAt: r?.lastMessageAt || createdAt,
    startedAt: r?.startedAt || createdAt,
    endedAt: r?.endedAt || null,
    client: r?.client || r?.user || "Unknown",
    userId: r?.userId || "",
    messages: Array.isArray(r?.messages) ? r.messages : [],
    messageCount: Number(r?.messageCount || (Array.isArray(r?.messages) ? r.messages.length : 0)),
    followUpCreated: Boolean(r?.followUpCreated || r?.followUp),
    issueSummary: r?.issueSummary || "",
    advicePreview: r?.advicePreview || "",
  };
}


function sentimentOf(text) {
  const t = (text || "").toLowerCase();
  let p = 0, n = 0;
  for (const w of POS) if (t.includes(w)) p++;
  for (const w of NEG) if (t.includes(w)) n++;
  if (n >= p + 2) return "negative";
  if (p >= n + 2) return "positive";
  return "neutral";
}

function sessionSentiment(rec) {
  const base = `${rec.issueSummary} ${rec.advicePreview} ${
    (rec.messages || []).slice(-4).map(m => m?.content || "").join(" ")
  }`;
  return sentimentOf(base);
}

function isFlagged(rec) {
  if (rec?.flagged === true) return true;
  return detectSessionFlag(rec);
}

function summaryPersistenceDisplay(summary = {}) {
  const status = String(summary?.persistenceStatus || "").trim().toLowerCase();
  if (status === "pending") return { label: "Summary save pending", error: "" };
  if (status === "persisted") return { label: "Summary saved", error: "" };
  if (status === "failed") {
    return {
      label: "Summary save failed",
      error: String(summary?.persistenceError || "The generated summary was not saved to the shared session record.").trim(),
    };
  }
  return null;
}

function dayKey(timestamp) {
  const value = Number(timestamp || Date.now());
  const date = new Date(value);
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dayLabel(timestamp) {
  return new Date(Number(timestamp || Date.now())).toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function flagCaseSortPriority(row = {}) {
  const status = String(row.status || "open").toLowerCase();
  if ((status === "open" || status === "escalated") && !row.claimedBy) return 0;
  if (status === "claimed") return 1;
  if (status === "coach_message_sent") return 2;
  if (status === "escalated") return 3;
  return 4;
}

function flagCaseClaimDisplay(row = {}) {
  if (!row.claimedBy) return "Unclaimed";
  if (String(row.status || "").toLowerCase() === "coach_message_sent") return "Contacted";
  return "Claimed";
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, activeOrgId } = useAuth();
  const [remoteSessions, setRemoteSessions] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [flagCases, setFlagCases] = useState([]);
  const [followUpFilter, setFollowUpFilter] = useState("open");
  const [sessionVisibleCount, setSessionVisibleCount] = useState(10);
  const [followUpStatus, setFollowUpStatus] = useState("");
  const [followUpBusyId, setFollowUpBusyId] = useState("");
  const isSuperActual = user?.role === "superadmin";
  const isInvestorActual = user?.role === "investor";
  const isCoachActual = user?.role === "coach";
  const canSeeFollowUpQueue = isCoachActual || isSuperActual;
  const orgId = activeOrgId || user?.orgId || "org_default";

  useEffect(() => {
    let mounted = true;
    if (!user?.uid) return undefined;
    fetchOrgSessionsFiltered(user, orgId, { role: "all", days: "90" })
      .then((sessions) => {
        if (!mounted) return;
        const rows = Array.isArray(sessions) ? sortSessionsByRecency(sessions.map(normalizeRecord)) : [];
        logSessionListDiagnostics("coach_dashboard_remote_sessions", rows, "firestore:org_sessions");
        setRemoteSessions(rows);
      })
      .catch(() => {});
    fetchCheckinWindow(orgId, 7)
      .then((rows) => {
        if (!mounted) return;
        setCheckins(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {});
    const refreshFlagCases = () => {
      fetchFlagCases(user, orgId, { max: 80, includeResolved: true })
        .then((rows) => {
          if (!mounted) return;
          setFlagCases(Array.isArray(rows) ? rows : []);
        })
        .catch(() => {
          if (mounted) setFlagCases([]);
        });
    };
    refreshFlagCases();
    const flagInterval = setInterval(refreshFlagCases, 30000);
    return () => {
      mounted = false;
      clearInterval(flagInterval);
    };
  }, [user, orgId]);

  const unresolvedFlagCases = useMemo(
    () => flagCases
      .filter((row) => !isFinalFlagStatus(row.status))
      .sort((a, b) => {
        const priority = flagCaseSortPriority(a) - flagCaseSortPriority(b);
        if (priority !== 0) return priority;
        return Number(b.updatedAt || b.createdAt || 0) - Number(a.updatedAt || a.createdAt || 0);
      }),
    [flagCases]
  );
  const unresolvedFlagCaseSessionIds = useMemo(
    () => new Set(unresolvedFlagCases.map((row) => String(row.sessionId || "")).filter(Boolean)),
    [unresolvedFlagCases]
  );

  const data = useMemo(() => {
    const seen = new Map();
    for (const s of remoteSessions) {
      const rec = normalizeRecord(s);
      if (!seen.has(String(rec.id))) seen.set(String(rec.id), rec);
    }
    const sessions = sortSessionsByRecency(Array.from(seen.values()));
    const last7 = sessions.filter(s => Date.now() - getSessionRecencyTimestamp(s) < 7*24*3600*1000);
    const avgMsgs =
      sessions.length === 0
        ? 0
        : Math.round(
            sessions.reduce((sum, s) => sum + Number(s.messageCount || (Array.isArray(s.messages) ? s.messages.length : 0)), 0) /
              sessions.length
          );
    const followUpCount = sessions.filter((s) => s.followUpCreated || s.followUp).length;
    const followUpRate = sessions.length ? Math.round((followUpCount / sessions.length) * 100) : 0;

    const flagged = sessions.filter(isFlagged);
    const flaggedRate = sessions.length ? Math.round((flagged.length / sessions.length) * 100) : 0;
    const sessionsWithoutFollowUp = sessions.length - followUpCount;
    const followUpsCompleted = sessions.filter((s) => String(s.followUp?.status || "").toLowerCase() === "completed").length;
    const followUpsCreated = followUpCount;

    // Per-user rollup
    const users = new Map();
    for (const s of sessions) {
      const userKey = s.userId || s.userEmail || s.client || "Unknown";
      const displayClient = s.client || "Unknown";
      const prev = users.get(userKey) || {
        key: userKey,
        client: displayClient,
        totalSessions: 0,
        sessionsLast7Days: 0,
        lastActive: 0,
        lastSentiment: "neutral",
        lastSessionId: null,
      };
      prev.totalSessions += 1;
      const recency = getSessionRecencyTimestamp(s);
      if (Date.now() - recency < 7 * 24 * 3600 * 1000) {
        prev.sessionsLast7Days += 1;
      }
      if (recency > prev.lastActive) {
        prev.lastActive = recency;
        prev.lastSentiment = sessionSentiment(s);
        prev.lastSessionId = s.id;
        prev.client = displayClient;
      }
      users.set(userKey, prev);
    }
    const userRows = Array.from(users.values()).sort((a,b)=> b.lastActive - a.lastActive);
    const activeUsersLast7Days = userRows.filter((entry) => entry.lastActive >= Date.now() - 7 * 24 * 3600 * 1000).length;
    const returningUsers = userRows.filter((entry) => entry.totalSessions > 1).length;

    const sessionsPerDay = Array.from({ length: 7 }, (_, index) => {
      const timestamp = Date.now() - (6 - index) * 24 * 3600 * 1000;
      const key = dayKey(timestamp);
      const count = last7.filter((session) => dayKey(getSessionRecencyTimestamp(session)) === key).length;
      return {
        key,
        label: dayLabel(timestamp),
        count,
      };
    });

    const sentimentCounts = sessions.reduce(
      (acc, session) => {
        const key = sessionSentiment(session);
        acc[key] += 1;
        return acc;
      },
      { positive: 0, neutral: 0, negative: 0 }
    );

    const checkinCount = checkins.length;
    const avgMood =
      checkinCount === 0
        ? 0
        : Math.round(
            (checkins.reduce((sum, c) => sum + Number(c.mood || 0), 0) / checkinCount) * 10
          ) / 10;
    const tagCounts = {};
    checkins.forEach((c) => {
      (c.tags || []).forEach((tag) => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    const topTags = Object.entries(tagCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([tag]) => tag);

    return {
      sessions,
      last7Count: last7.length,
      total: sessions.length,
      avgMsgs,
      followUpCount,
      followUpRate,
      activeUsersLast7Days,
      returningUsers,
      flaggedRate,
      followUpsCreated,
      followUpsCompleted,
      sessionsWithoutFollowUp,
      sessionsPerDay,
      flagged,
      users: userRows,
      sentimentCounts,
      checkinCount,
      avgMood,
      topTags,
    };
  }, [remoteSessions, checkins]);

  const sentimentData = [
    { name: "Positive", value: data.sentimentCounts.positive },
    { name: "Neutral", value: data.sentimentCounts.neutral },
    { name: "Negative", value: data.sentimentCounts.negative },
  ];

  const followUpRows = useMemo(
    () => buildFollowUpQueueRows(remoteSessions, { coachRequiredSessionIds: unresolvedFlagCaseSessionIds }),
    [remoteSessions, unresolvedFlagCaseSessionIds]
  );
  const actionableFollowUpRows = useMemo(
    () => followUpRows.filter((row) => row.coachActionRequired),
    [followUpRows]
  );

  const filteredFollowUpRows = useMemo(() => {
    const rows = actionableFollowUpRows;
    if (followUpFilter === "open") {
      return rows.filter((row) =>
        (row.followUpDisplayStatus === "scheduled" || row.followUpDisplayStatus === "resumed") &&
        row.followUp.status !== "completed" &&
        row.followUp.status !== "dismissed"
      );
    }
    if (followUpFilter === "scheduled") {
      return rows.filter((row) => row.followUpDisplayStatus === "scheduled");
    }
    if (followUpFilter === "resumed") {
      return rows.filter((row) => row.followUpDisplayStatus === "resumed");
    }
    if (followUpFilter === "suggested") {
      return rows.filter((row) => row.followUp.status === "suggested" && !row.followUp.scheduledFor);
    }
    if (followUpFilter === "all") return rows;
    return rows.filter((row) => row.followUp.status === followUpFilter);
  }, [actionableFollowUpRows, followUpFilter]);
  const viewerRole = user?.role || "member";
  const displayNameForSession = useCallback(
    (session, fallback = "Member") => getSessionDisplayName(session, viewerRole, fallback),
    [viewerRole]
  );
  const maskSessionText = useCallback(
    (session, text) => getSessionDisplayText(text, session || {}, viewerRole),
    [viewerRole]
  );

  const persistFollowUpStatus = useCallback(async (row, status) => {
    if (!user?.uid || !row?.sessionId || !canSeeFollowUpQueue) return;
    setFollowUpBusyId(String(row.sessionId));
    setFollowUpStatus("");
    try {
      const stamped = new Date().toISOString();
      const finalStatusPatch = status === "completed"
        ? {
            completedAt: stamped,
            dismissedAt: null,
          }
        : status === "dismissed"
          ? {
              dismissedAt: stamped,
              completedAt: null,
            }
          : {
              completedAt: null,
              dismissedAt: null,
            };
      const nextFollowUp = mergeSessionFollowUp(
        {
          ...row.followUp,
          status,
          ...finalStatusPatch,
          updatedAt: stamped,
          updatedByType: "coach",
        },
        row.followUp,
        null
      );
      await setDoc(
        doc(db, "orgs", orgId, "sessions", String(row.sessionId)),
        { followUp: nextFollowUp },
        { merge: true }
      );
      setRemoteSessions((prev) =>
        prev.map((session) =>
          String(session.id) === String(row.sessionId)
            ? { ...session, followUp: nextFollowUp }
            : session
        )
      );
      setFollowUpStatus(status === "completed" ? "Follow-up marked completed." : "Follow-up dismissed.");
    } catch {
      setFollowUpStatus("Unable to update follow-up right now.");
    } finally {
      setFollowUpBusyId("");
    }
  }, [canSeeFollowUpQueue, orgId, user]);

  return (
    <div className="nia-page">
      <Card className="nia-hero">
        <div className="nia-header">
          <div>
            <h2>Coach Intelligence Console</h2>
            <p>Safety signals, coaching load, and sentiment shifts across live conversations.</p>
          </div>
          <div className="nia-header-actions">
            <button className="btn btn-ghost" onClick={() => window.location.reload()}>
              Refresh Insights
            </button>
          </div>
        </div>
      </Card>

      <div className="nia-grid nia-grid--4">
        <Stat label="Total Sessions" value={data.total} subtle="All-time conversations" />
        <Stat label="Avg Messages" value={data.avgMsgs} subtle="Per session depth" />
        <Stat label="Follow-up Rate" value={`${data.followUpRate}%`} subtle={`${data.followUpCount} sessions with follow-up`} />
        <Stat label="Active Users (7 days)" value={data.activeUsersLast7Days} subtle="Members with recent sessions" />
      </div>
      <PilotOperationsSection user={user} orgId={orgId} />

      <div className="nia-grid nia-grid--3" style={{ marginTop: 16 }}>
        <Card title="Engagement">
          <div className="nia-grid nia-grid--2" style={{ marginBottom: 12 }}>
            <Stat label="Sessions (7 days)" value={data.last7Count} subtle="Recent activity" />
            <Stat label="Returning Users" value={data.returningUsers} subtle="More than one session" />
          </div>
          <Table
            headers={["Day", "Sessions"]}
            rows={data.sessionsPerDay.map((entry) => [entry.label, entry.count])}
          />
        </Card>

        <Card title="Behaviour">
          <div className="nia-grid nia-grid--2">
            <Stat label="With Follow-up" value={`${data.followUpRate}%`} subtle={`${data.followUpCount} sessions`} />
            <Stat label="Open Review Cases" value={unresolvedFlagCases.length} subtle="Unresolved coach actions" />
          </div>
        </Card>

        <Card title="Coach Activity">
          <div className="nia-grid nia-grid--2">
            <Stat label="Follow-ups Created" value={data.followUpsCreated} subtle="Sessions with follow-up" />
            <Stat label="Follow-ups Completed" value={data.followUpsCompleted} subtle="Completed status" />
          </div>
        </Card>
      </div>

      <div className="nia-grid nia-grid--2" style={{ marginTop: 16 }}>
        <Card title="Risk">
          <div className="nia-grid nia-grid--2">
            <Stat label="Flagged Sessions" value={data.flagged.length} subtle={`${data.flaggedRate}% of sessions`} />
            <Stat label="Open Review Cases" value={unresolvedFlagCases.length} subtle="Source of truth for coach review" />
            <Stat label="Without Follow-up" value={data.sessionsWithoutFollowUp} subtle="Sessions still without follow-up" />
          </div>
        </Card>
      </div>

      {(isSuperActual || isInvestorActual) && (
        <Card title="Investor Materials" style={{ marginBottom: 16 }}>
          <div className="nia-list">
            <div className="nia-list-btn" style={{ cursor: "default" }}>
              <span>Investor Showcase</span>
              <span className="nia-pill">Deck</span>
              <button className="btn btn-ghost" onClick={() => navigate("/investor")}>
                Open
              </button>
            </div>
            <div className="nia-list-btn" style={{ cursor: "default" }}>
              <span>Demo Walkthrough</span>
              <span className="nia-pill">Guided</span>
              <button className="btn btn-ghost" onClick={() => navigate("/demo")}>
                Start
              </button>
            </div>
          </div>
        </Card>
      )}

      <div className="nia-grid nia-grid--2">
        {canSeeFollowUpQueue && (
          <Card title="Follow-up Queue">
            <div className="dashboard-section-head">
              <div className="nia-subtitle">Coaching follow-up tasks only. Safety/review cases are in the intervention queue.</div>
              <span className="nia-pill">{filteredFollowUpRows.length} in view</span>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <button className="btn btn-ghost" onClick={() => setFollowUpFilter("open")}>Open</button>
              <button className="btn btn-ghost" onClick={() => setFollowUpFilter("all")}>All</button>
              <button className="btn btn-ghost" onClick={() => setFollowUpFilter("scheduled")}>Scheduled</button>
              <button className="btn btn-ghost" onClick={() => setFollowUpFilter("resumed")}>Resumed</button>
              <button className="btn btn-ghost" onClick={() => setFollowUpFilter("suggested")}>Suggested</button>
              <button className="btn btn-ghost" onClick={() => setFollowUpFilter("completed")}>Completed</button>
              <button className="btn btn-ghost" onClick={() => setFollowUpFilter("dismissed")}>Dismissed</button>
            </div>
            {followUpStatus ? <div className="nia-muted" style={{ marginBottom: 10 }}>{followUpStatus}</div> : null}
            <Table
              headers={["Member / Session", "Next step", "Timing", "Status", "Actions"]}
              rows={
                filteredFollowUpRows.length
                  ? filteredFollowUpRows.map((row) => [
                      <div style={{ display: "grid", gap: 4 }}>
                        <strong>{displayNameForSession(row.session || { id: row.sessionId, client: row.client })}</strong>
                        <div className="nia-muted">{maskSessionText(row.session || { id: row.sessionId, client: row.client }, row.title)}</div>
                        <div className="nia-muted">
                          Source {row.followUp.createdByType === "coach" ? "Coach" : "Nia"}
                          {" / "}
                          Updated {row.followUp.updatedByType === "coach" ? "Coach" : "Nia"}
                        </div>
                      </div>,
                      <div style={{ minWidth: 0 }}>
                        <div>{maskSessionText(row.session || { id: row.sessionId, client: row.client }, formatSupportTextForAudience(row.followUp.nextStepText, "reviewer"))}</div>
                        {row.followUp.reminderSentAt ? (
                          <div className="nia-muted" style={{ marginTop: 4 }}>
                            Reminder sent {formatFollowUpDate(row.followUp.reminderSentAt)}
                          </div>
                        ) : null}
                      </div>,
                      <div style={{ display: "grid", gap: 4 }}>
                        <div>{row.followUp.scheduledFor ? formatFollowUpDate(row.followUp.scheduledFor) : "Not scheduled"}</div>
                        <div className="nia-muted">
                          {row.followUpDisplayStatus === "resumed"
                            ? `Member resumed ${formatFollowUpDate(row.followUp.resumedAt || row.followUp.lastOpenedAt)}`
                            : row.followUp.reminderSentAt ? "Reminder sent" : "Reminder pending"}
                        </div>
                      </div>,
                      <span className="nia-pill">{String(getFollowUpDisplayStatus(row.followUp) || "suggested").toUpperCase()}</span>,
                      <div className="table-actions">
                        <button className="btn btn-ghost" onClick={() => navigate(`/chat?id=${row.sessionId}&resume=followup`)}>
                          Open
                        </button>
                        <button
                          className="btn btn-ghost"
                          onClick={() => persistFollowUpStatus(row, "completed")}
                          disabled={followUpBusyId === String(row.sessionId) || row.followUp.status === "completed"}
                        >
                          Complete
                        </button>
                        <button
                          className="btn btn-ghost"
                          onClick={() => persistFollowUpStatus(row, "dismissed")}
                          disabled={followUpBusyId === String(row.sessionId) || row.followUp.status === "dismissed"}
                        >
                          Dismiss
                        </button>
                      </div>,
                    ])
                  : [["-", "No follow-ups in this view.", "-", "-", "-"]]
              }
            />
          </Card>
        )}

        <Card title="Sentiment Mix">
          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height="100%" minWidth={10} minHeight={10} debounce={120}>
              <BarChart data={sentimentData}>
                <XAxis dataKey="name" stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                <YAxis stroke="var(--ui-muted)" tick={{ fill: "var(--ui-muted)", fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    background: "var(--ui-surface)",
                    border: "1px solid var(--ui-border)",
                    color: "var(--ui-text)",
                  }}
                />
                <Bar dataKey="value" fill="var(--ui-accent)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card title="Intervention Queue — Open Review Cases">
          <div className="dashboard-section-head">
            <div className="nia-subtitle">Claimable safety and operational-support cases. Separate from coaching follow-ups.</div>
            <span className="nia-pill">{unresolvedFlagCases.length} open</span>
          </div>
          <Table
            headers={["Created", "Client", "Reason", "Status", "Claim", "Open"]}
            rows={
              unresolvedFlagCases.length
                ? unresolvedFlagCases.slice(0, 10).map((row) => [
                    new Date(Number(row.createdAt || Date.now())).toLocaleDateString(),
                    displayNameForSession({ id: row.sessionId, client: row.sessionUser, user: row.sessionUser }),
                    String(row.reviewReason || row.reviewKind || row.caseType || "flagged").replace(/_/g, " "),
                    <span className="nia-pill">{getFlagCaseStatusLabel(row)}</span>,
                    flagCaseClaimDisplay(row),
                    <button className="btn btn-ghost" onClick={() => navigate(`/chat?id=${row.sessionId}`)}>Open</button>,
                  ])
                : [["-", "-", "-", "-", "No unresolved cases.", "-"]]
            }
          />
        </Card>
      </div>

      <div className="nia-grid nia-grid--2">
        <Card title="Weekly Check‑ins">
          <div className="nia-grid nia-grid--3">
            <Stat label="Check‑ins" value={data.checkinCount} subtle="Last 7 days" />
            <Stat label="Avg mood" value={data.avgMood || 0} subtle="1–5 scale" />
            <Stat label="Top themes" value={data.topTags.join(", ") || "—"} subtle="Most common tags" />
          </div>
          {(isCoachActual || isSuperActual) && (
            <Table
              headers={["User", "Mood", "Tags", "Date"]}
              rows={
                checkins.length
                  ? checkins.slice(0, 6).map((c) => [
                      c.userName || "User",
                      c.moodLabel || "Okay",
                      (c.tags || []).slice(0, 3).join(", "),
                      new Date(c.createdAt).toLocaleDateString(),
                    ])
                  : [["-", "-", "-", "No check‑ins yet."]]
              }
            />
          )}
          {(isInvestorActual || (!isCoachActual && !isSuperActual)) && (
            <div className="nia-muted" style={{ marginTop: 10 }}>
              Admin view is anonymised. Only aggregate trends are shown.
            </div>
          )}
        </Card>
      </div>

      <Card title="Recent Sessions">
        <div className="dashboard-section-head">
          <div className="nia-subtitle">Sorted by last message, then update, then creation</div>
          <span className="nia-pill">{Math.min(sessionVisibleCount, data.sessions.length)}/{data.sessions.length} shown</span>
        </div>
        <Table
          headers={[
            "Member",
            "Last Activity",
            "Messages",
            "Session",
            "Summary",
            "Delivery / Reminder",
            "Open",
          ]}
          rows={
            data.sessions.length
              ? data.sessions.slice(0, sessionVisibleCount).map((session) => {
                  const currentSummary = resolveCurrentRecordSummary(session);
                  const continuation = normalizePostSessionContinuation(session);
                  const trustedContinuation =
                    continuation.source === "post_session_continuation";
                  const persistence = trustedContinuation
                    ? null
                    : summaryPersistenceDisplay(session.sessionSummary);
                  const finalization = (
                    continuation.finalization.status === "ready" &&
                    !currentSummary?.summaryText
                  )
                    ? {
                        ...continuation.finalization,
                        status: "failed",
                        lastError: "No current canonical summary is available for display.",
                      }
                    : continuation.finalization;
                  const summaryText = currentSummary?.summaryText ||
                    "No current canonical summary is available.";
                  return [
                    displayNameForSession(session),
                    new Date(getSessionRecencyTimestamp(session) || session.createdAt || Date.now()).toLocaleString(),
                    Number(session.messageCount || session.messages?.length || 0),
                    <span className="nia-pill">{getSessionLifecycleLabel(session)}</span>,
                    <div style={{ display: "grid", gap: 4 }}>
                      <span className="nia-pill">
                        {getPostSessionFinalizationLabel(finalization)}
                      </span>
                      {persistence ? <span className="nia-pill">{persistence.label}</span> : null}
                      {persistence?.error ? <span className="nia-muted">{persistence.error}</span> : null}
                      <span>{maskSessionText(session, summaryText)}</span>
                    </div>,
                    <div style={{ display: "grid", gap: 4 }}>
                      <span className="nia-pill">
                        {getPostSessionAutomaticEmailLabel(continuation)}
                      </span>
                      <span className="nia-pill">
                        {getPostSessionManualResendLabel(continuation)}
                      </span>
                      <span className="nia-pill">
                        {getPostSessionReminderLabel(continuation)}
                      </span>
                    </div>,
                    <button className="btn btn-ghost" onClick={() => navigate(`/chat?id=${session.id}`)}>Open</button>,
                  ];
                })
              : [[
                  "-",
                  "-",
                  "-",
                  "No sessions yet.",
                  "No current canonical summary.",
                  "Automatic email not started",
                  "-",
                ]]
          }
        />
        {data.sessions.length > sessionVisibleCount ? (
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-ghost" onClick={() => setSessionVisibleCount((count) => count + 10)}>
              View more sessions
            </button>
          </div>
        ) : sessionVisibleCount > 10 ? (
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-ghost" onClick={() => setSessionVisibleCount(10)}>
              Show fewer sessions
            </button>
          </div>
        ) : null}
      </Card>

      <Card title="User Risk & Activity">
        <Table
          headers={["User", "Sessions", "Last Active", "Last Sentiment", "Actions"]}
          rows={
            data.users.length
              ? data.users.map((u) => [
                  displayNameForSession({ id: u.lastSessionId, client: u.client }),
                  u.totalSessions,
                  new Date(u.lastActive).toLocaleDateString(),
                  u.lastSentiment,
                  <div className="table-actions">
                    <button className="btn btn-ghost" onClick={() => navigate(`/chat?id=${u.lastSessionId}`)}>
                      Open Latest
                    </button>
                    <button className="btn btn-ghost" onClick={() => navigate(`/chat?id=${u.lastSessionId}&resume=followup`)}>
                      Follow-up
                    </button>
                  </div>,
                ])
              : [["-", "-", "-", "-", "No users yet."]]
          }
        />
      </Card>
    </div>
  );
}
